import { __ } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, RichText, InspectorControls } from '@wordpress/block-editor';
import { 
    PanelBody, 
    SelectControl,
    TabPanel
} from '@wordpress/components';

// Import custom components
import Typography from '../../components/Typography';
import VisibilityControl from '../../components/VisibilityControl';
import FlatColorPicker from '../../components/FlatColorPicker';
import Spacing from '../../components/Spacing';
import Border from '../../components/Border';
import { getSpacingStyles, getBorderStyles, getTypographyStyles } from '../../utils/styles';

registerBlockType('kanews/alert', {
    apiVersion: 2,
    title: __('Bilgi Kutusu', 'kanews'),
    icon: 'warning',
    category: 'kanews',
    attributes: {
        content: {
            type: 'string',
            default: __('Alert içeriği buraya gelecek', 'kanews')
        },
        type: {
            type: 'string',
            default: 'success'
        },
        backgroundColor: {
            type: 'string',
            default: ''
        },
        typography: {
            type: 'object',
            default: {
                fontSize: '',
                fontFamily: '',
                fontWeight: '',
                lineHeight: '',
                letterSpacing: ''
            }
        },
        margin: {
            type: 'object',
            default: {}
        },
        padding: {
            type: 'object',
            default: {}
        },
        border: {
            type: 'object',
            default: {}
        },
        visibility: {
            type: 'object',
            default: {
                desktop: false,
                tablet: false,
                mobile: false
            }
        }
    },
    edit: ({ attributes, setAttributes }) => {
        const blockProps = useBlockProps();
        const { 
            content, 
            type, 
            backgroundColor,
            typography,
            margin,
            padding,
            border,
            visibility
        } = attributes;


        const visibilityClasses = [
            visibility?.desktop ? 'hidden-desktop' : '',
            visibility?.tablet ? 'hidden-tablet' : '',
            visibility?.mobile ? 'hidden-mobile' : ''
        ].filter(Boolean);

        const wrapperProps = {
            className: ['kanews-alert-wrapper', ...visibilityClasses].filter(Boolean).join(' ')
        };

        const tabs = [
            {
                name: 'content',
                title: __('İçerik', 'kanews'),
                className: 'content-tab'
            },
            {
                name: 'design',
                title: __('Tasarım', 'kanews'),
                className: 'design-tab'
            },
            {
                name: 'visibility',
                title: __('Görünürlük', 'kanews'),
                className: 'visibility-tab'
            }
        ];

        return (
            <>
                <InspectorControls>
                    <TabPanel
                        className="kanews-tab-panel components-base-control"
                        tabs={tabs}
                    >
                        {(tab) => {
                            return (
                                <>
                                    {tab.name === 'content' && (
                                        <PanelBody>
                                            <SelectControl
                                                label={__('Alert Türü', 'kanews')}
                                                value={type}
                                                options={[
                                                    { label: __('Başarılı', 'kanews'), value: 'success' },
                                                    { label: __('Bilgi', 'kanews'), value: 'info' },
                                                    { label: __('Uyarı', 'kanews'), value: 'warning' },
                                                    { label: __('Hata', 'kanews'), value: 'danger' }
                                                ]}
                                                onChange={(value) => setAttributes({ type: value })}
                                            />
                                        </PanelBody>
                                    )}
                                    {tab.name === 'design' && (
                                        <>
                                            <PanelBody title={__('Tipografi', 'kanews')}>
                                                <Typography
                                                    value={typography}
                                                    showColor={true}
                                                    onChange={(value) => setAttributes({ typography: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Renkler', 'kanews')} initialOpen={false}>
                                                <FlatColorPicker
                                                    label={__('Arka Plan Rengi', 'kanews')}
                                                    value={backgroundColor || ''}
                                                    onChange={(value) => setAttributes({ backgroundColor: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Kenarlık', 'kanews')} initialOpen={false}>
                                                <Border
                                                    label={__('Kenarlık', 'kanews')}
                                                    value={border}
                                                    onChange={(value) => setAttributes({ border: value })}
                                                />
                                            </PanelBody>
                                            <PanelBody title={__('Boşluklar', 'kanews')} initialOpen={false}>
                                                <Spacing
                                                    label={__('Dış Boşluk', 'kanews')}
                                                    value={margin}
                                                    onChange={(value) => setAttributes({ margin: value })}
                                                />
                                                <Spacing
                                                    label={__('İç Boşluk', 'kanews')}
                                                    value={padding}
                                                    onChange={(value) => setAttributes({ padding: value })}
                                                />
                                            </PanelBody>
                                        </>
                                    )}
                                    {tab.name === 'visibility' && (
                                        <PanelBody>
                                            <VisibilityControl
                                                value={visibility}
                                                onChange={(value) => setAttributes({ visibility: value })}
                                            />
                                        </PanelBody>
                                    )}
                                </>
                            );
                        }}
                    </TabPanel>
                </InspectorControls>

                <div {...blockProps}>
                    <div {...wrapperProps}>
                        <div 
                            className={`kanews-response kanews-response-${type} kanews-block-padding kanews-block-border kanews-block-margin`}
                            style={{...getTypographyStyles(typography), ...getSpacingStyles(padding, 'padding'), ...getBorderStyles(border, 'border'), ...getSpacingStyles(margin, 'margin'),
                                backgroundColor: backgroundColor,
                            }}
                        >
                            <RichText
                                tagName="div"
                                value={content}
                                onChange={(value) => setAttributes({ content: value })}
                                placeholder={__('Alert içeriği buraya gelecek', 'kanews')}
                                className="kanews-response-content"
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    },
    save: ({ attributes }) => {
        const { 
            content, 
            type, 
            backgroundColor,
            typography,
            margin,
            padding,
            border,
            visibility
        } = attributes;

        const visibilityClasses = [
            visibility?.desktop ? 'hidden-desktop' : '',
            visibility?.tablet ? 'hidden-tablet' : '',
            visibility?.mobile ? 'hidden-mobile' : ''
        ].filter(Boolean);

        const wrapperProps = {
            className: ['wp-block-kanews-alert', 'kanews-alert-wrapper', ...visibilityClasses].filter(Boolean).join(' ')
        };

        return (
            <div {...wrapperProps}>
                <div 
                    className={`kanews-response kanews-response-${type} kanews-block-padding kanews-block-border kanews-block-margin`}
                    style={{...getTypographyStyles(typography), ...getSpacingStyles(padding, 'padding'), ...getBorderStyles(border, 'border'), ...getSpacingStyles(margin, 'margin'),
                        backgroundColor: backgroundColor,
                    }}
                >
                    <div className="kanews-response-content">
                        {content}
                    </div>
                </div>
            </div>
        );
    }
}); 